'use strict';

import { environment } from '@quartz-iam-legacy/environments/environment';

/**
 * Authentication service facade.
 * Delegates to either Auth0 or Keycloak provider based on environment configuration.
 *
 * This service provides a consistent authentication API regardless of the underlying provider.
 * The actual provider is selected at runtime based on environment.authProvider.
 *
 * @module services.auth
 */
angular.module('services.auth', [
  'services.auth0Provider',
  'services.keycloakProvider',
  'services.keycloakInterceptor'
])

.factory('authService', ['auth0Provider', 'keycloakProvider', function(auth0Provider, keycloakProvider) {
  var service = {};

  // Select authentication provider based on environment
  var authProvider = environment.authProvider || 'auth0';
  var provider = authProvider === 'keycloak' ? keycloakProvider : auth0Provider;

  /**
   * Initialize the authentication provider.
   * Must be called before using the service (typically in app.run).
   *
   * @param {object} options - Optional initialization options
   * @param {boolean} options.forceLogin - Force immediate login redirect (Keycloak only)
   * @returns {Promise<boolean>} Promise that resolves when initialization is complete
   */
  service.init = function(options) {
    return provider.init(options);
  };

  /**
   * Check if user is authenticated.
   *
   * @returns {Promise<boolean>} Promise that resolves to authentication status
   */
  service.isAuthenticated = function() {
    return provider.isAuthenticated();
  };

  /**
   * Get current user profile.
   *
   * @returns {Promise<object|null>} Promise that resolves to user profile or null
   */
  service.user = function() {
    return provider.user();
  };

  /**
   * Get ID token (Auth0-specific).
   * For Keycloak, returns the access token instead.
   *
   * @returns {Promise<string>} Promise that resolves to token
   * @deprecated Use getAccessToken() instead for cross-provider compatibility
   */
  service.getIdToken = function() {
    // Both providers use access tokens, so this returns the same as getAccessToken
    return provider.getAccessToken();
  };

  /**
   * Get access token.
   * Returns a fresh, non-expired token (automatically refreshes if needed).
   *
   * @returns {Promise<string>} Promise that resolves to access token
   */
  service.getAccessToken = function() {
    return provider.getAccessToken();
  };

  /**
   * Initiate login flow.
   * Redirects to authentication provider's login page.
   *
   * @param {string|null} appStateTarget - URL to return to after successful login (optional)
   */
  service.login = function(appStateTarget) {
    return provider.login(appStateTarget);
  };

  /**
   * Log out current user.
   * Clears session and redirects to origin.
   */
  service.logout = function() {
    return provider.logout();
  };

  /**
   * Handle OAuth callback (Auth0-specific).
   * For Keycloak, this is a no-op as it handles callbacks internally.
   *
   * @returns {Promise<string|null>} Promise that resolves to target route or null
   */
  service.handleAuthentication = function() {
    return provider.handleAuthentication();
  };

  /**
   * Get user profile with callback (legacy API).
   * Provided for backward compatibility.
   *
   * @param {function} cb - Callback function(error, profile)
   * @deprecated Use user() instead for promise-based API
   */
  service.getProfile = function(cb) {
    provider.user()
      .then(function(profile) {
        cb(null, profile);
      })
      .catch(function(error) {
        cb(error, null);
      });
  };

  /**
   * Get cached user profile (synchronous).
   * Not supported in this facade - always returns null.
   *
   * @returns {null}
   * @deprecated Use user() instead for promise-based API
   */
  service.getCachedProfile = function() {
    console.warn('[authService] getCachedProfile() is deprecated and not supported in facade pattern. Use user() instead.');
    return null;
  };

  /**
   * Manually renew tokens (Auth0-specific).
   * For Keycloak, tokens are automatically refreshed.
   *
   * @deprecated Tokens are automatically refreshed by both providers
   */
  service.renewTokens = function() {
    if (authProvider === 'auth0') {
      console.warn('[authService] renewTokens() is deprecated. Auth0 automatically refreshes tokens.');
    } else {
      console.log('[authService] renewTokens() called with Keycloak - tokens are auto-refreshed');
    }
    // No-op - both providers handle token refresh automatically
  };

  /**
   * Schedule automatic token renewal (Auth0-specific).
   * For Keycloak, renewal is already handled automatically.
   *
   * @deprecated Tokens are automatically refreshed by both providers
   */
  service.scheduleRenewal = function() {
    if (authProvider === 'auth0') {
      console.warn('[authService] scheduleRenewal() is deprecated. Auth0 automatically refreshes tokens.');
    } else {
      console.log('[authService] scheduleRenewal() called with Keycloak - already handled automatically');
    }
    // No-op - both providers handle token refresh automatically
  };

  return service;
}]);
